import type { FilterCondition, LogicOperator } from "@repo/game-admin"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import type { ComponentProps } from "react"
import * as React from "react"
import { CreateEntityModal } from "@/components/create-content/create-entity-modal"
import { DataTable } from "@/components/data-table/data-table"
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header"
import type { DataTableColumnDef } from "@/components/data-table/data-table-features"
import { DataTableRowActions } from "@/components/data-table/data-table-row-actions"
import { DataTableFilteredViewsToggle } from "@/components/data-table/filtered-views/data-table-filtered-views-toggle"
import { PageHeader } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { PAGINATION } from "@/config/constants"
import { useStagingExpansionGroups } from "@/queries/expansion-groups/use-staging-expansion-groups"
import { useFilterOptions } from "@/queries/metadata/use-filter-options"
import type { StagingExpansionGroup } from "@/types/expansion-groups.types"
import { filtersToSearchParams, parseUrlParam } from "@/utils/url-params"

export const Route = createFileRoute("/expansion-groups/")({
	component: ExpansionGroupsPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			gameSlug: search.gameSlug as string | undefined,
			...(search.create === "1" || search.create === true
				? { create: true }
				: {}),
		}
	},
})

const getColumns = (): DataTableColumnDef<StagingExpansionGroup>[] => [
	{
		id: "select",
		header: ({ table }) => (
			<Checkbox
				checked={
					table.getIsAllPageRowsSelected() ||
					(table.getIsSomePageRowsSelected() && "indeterminate")
				}
				onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
				aria-label="Select all"
				className="translate-y-[2px]"
			/>
		),
		cell: ({ row }) => (
			<Checkbox
				checked={row.getIsSelected()}
				onCheckedChange={(value) => row.toggleSelected(!!value)}
				aria-label="Select row"
				className="translate-y-[2px]"
			/>
		),
		enableSorting: false,
		enableHiding: false,
	},
	{
		accessorKey: "name",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Name" />
		),
		cell: ({ row }) => {
			return (
				<span className="block max-w-[300px] truncate font-medium">
					{row.getValue("name")}
				</span>
			)
		},
	},
	{
		accessorKey: "slug",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Slug" />
		),
		cell: ({ row }) => {
			return (
				<span className="block max-w-[200px] truncate font-mono text-xs">
					{row.getValue("slug")}
				</span>
			)
		},
	},
	{
		accessorKey: "gameSlug",
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title="Game" />
		),
		cell: ({ row }) => {
			const gameSlug = row.getValue("gameSlug") as string
			return (
				<div className="flex items-center">
					<Badge variant={gameSlug as ComponentProps<typeof Badge>["variant"]}>
						{gameSlug}
					</Badge>
				</div>
			)
		},
		filterFn: (row, id, value) => {
			return value.includes(row.getValue(id))
		},
	},
	{
		id: "actions",
		cell: ({ row }) => (
			<DataTableRowActions
				row={row}
				actions={[
					{
						label: "View Details",
						linkProps: {
							to: "/expansion-groups/$id",
							params: { id: row.original._id.$oid },
							search: { gameSlug: row.original.gameSlug },
						},
					},
					{
						label: "Edit",
						linkProps: {
							to: "/expansion-groups/$id/manage",
							params: { id: row.original._id.$oid },
							search: { gameSlug: row.original.gameSlug },
						},
					},
					{
						label: "View Expansions",
						linkProps: {
							to: "/catalogue/games/$gameSlug",
							params: { gameSlug: row.original.gameSlug },
						},
						separator: true,
					},
				]}
			/>
		),
	},
]

function ExpansionGroupsPage() {
	const navigate = useNavigate()
	const searchParams = useSearch({ from: "/expansion-groups/" })
	const columns = React.useMemo(() => getColumns(), [])

	const [pageIndex, setPageIndex] = React.useState(0)
	const [pageSize, setPageSize] = React.useState(PAGINATION.DEFAULT_PAGE_SIZE)

	const [search, setSearch] = React.useState("")
	const [filters, setFilters] = React.useState<{
		gameSlug?: string[]
	}>({
		gameSlug: parseUrlParam(searchParams.gameSlug),
	})

	const [createModalOpen, setCreateModalOpen] = React.useState(false)
	const [selectedViewId, setSelectedViewId] = React.useState<
		string | undefined
	>(undefined)
	const [viewConditions, setViewConditions] = React.useState<
		FilterCondition[] | undefined
	>(undefined)
	const [viewLogic, setViewLogic] = React.useState<LogicOperator | undefined>(
		undefined,
	)

	React.useEffect(() => {
		if (!searchParams.create) return
		setCreateModalOpen(true)
		navigate({
			to: "/expansion-groups",
			search: { gameSlug: searchParams.gameSlug },
			replace: true,
		})
	}, [searchParams.create, navigate])

	const { expansionGroups, isLoading, error, count, pages } =
		useStagingExpansionGroups({
			page: pageIndex + 1,
			limit: pageSize,
			search,
			filters,
			viewConditions,
			viewLogic,
		})

	const { data: gameOptions = [] } = useFilterOptions({
		entityType: "game",
		field: "gameSlug",
	})

	const handlePaginationChange = React.useCallback(
		(newPageIndex: number, newPageSize: number) => {
			setPageIndex(newPageIndex)
			setPageSize(newPageSize as typeof PAGINATION.DEFAULT_PAGE_SIZE)
		},
		[],
	)

	const handleSearchChange = React.useCallback((value: string) => {
		setSearch(value)
		setPageIndex(0)
	}, [])

	const handleFilterChange = React.useCallback(
		(filterKey: string, value: string[] | undefined) => {
			setFilters((prev) => {
				const newFilters = {
					...prev,
					[filterKey]: value && value.length > 0 ? value : undefined,
				}

				const searchParams = filtersToSearchParams(newFilters)

				navigate({
					to: "/expansion-groups",
					search: {
						gameSlug: searchParams.gameSlug,
					},
					replace: true,
				})

				return newFilters
			})
			setPageIndex(0)
		},
		[navigate],
	)

	const handleViewSelect = React.useCallback(
		(viewId: string, conditions: FilterCondition[], logic: LogicOperator) => {
			setSelectedViewId(viewId)
			setViewConditions(conditions)
			setViewLogic(logic)
			setPageIndex(0)
		},
		[],
	)

	const handleViewClear = React.useCallback(() => {
		setSelectedViewId(undefined)
		setViewConditions(undefined)
		setViewLogic(undefined)
		setPageIndex(0)
	}, [])

	if (error) {
		return (
			<div className="flex flex-col gap-6 p-6">
				<PageHeader title="Expansion groups" />
				<p className="text-bad">
					Failed to load expansion groups: {error.message}
				</p>
			</div>
		)
	}

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader
				title="Expansion groups"
				count={isLoading ? undefined : count.toLocaleString()}
				actions={
					<Button onClick={() => setCreateModalOpen(true)}>
						<Plus />
						Create
					</Button>
				}
			/>

			<div className="min-h-0 flex-1">
				<DataTable
					actions={
						<DataTableFilteredViewsToggle
							entityType="expansionGroup"
							selectedViewId={selectedViewId}
							onViewSelect={handleViewSelect}
							onViewClear={handleViewClear}
						/>
					}
					columns={columns}
					data={expansionGroups}
					searchKey="expansion-groups"
					tableId="expansion-groups"
					facetedFilters={[
						{
							columnId: "gameSlug",
							title: "Game",
							options: gameOptions,
						},
					]}
					manualPagination
					pageCount={pages}
					pageIndex={pageIndex}
					pageSize={pageSize}
					totalCount={count}
					onPaginationChange={handlePaginationChange}
					manualFiltering
					searchValue={search}
					onSearchChange={handleSearchChange}
					onFilterChange={(columnId, value) => {
						handleFilterChange(
							columnId,
							Array.isArray(value) ? value : value ? [value] : undefined,
						)
					}}
					isLoading={isLoading}
				/>
			</div>

			<CreateEntityModal
				open={createModalOpen}
				setOpen={setCreateModalOpen}
				entityType="expansionGroup"
			/>
		</div>
	)
}
